import React, { useState } from 'react';
import {
  Box,
  Container,
  Card,
  CardContent,
  Typography,
  AppBar,
  Toolbar,
  Button,
  Chip,
  Alert,
  AlertTitle,
} from '@mui/material';
import {
  TrendingUp,
  AccountBalance,
  Lock,
  LockOpen,
  Logout,
  MonetizationOn,
  HourglassEmpty,
  CheckCircle,
  Cancel,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useLender } from '../../hooks/useLenders';
import { useRedemptions } from '../../hooks/useRedemptions';
import { Series } from '../../types';
import { formatCurrency, formatPercentage, isInLockupPeriod } from '../../utils/navCalculations';
import { format } from 'date-fns';
import RequestRedemptionDialog from '../../components/dialogs/RequestRedemptionDialog';

const InvestorDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { userData } = useAuth(); // Get authenticated user data
  const { lender: myLender, loading: loadingLender } = useLender(userData?.lenderId || '');
  const [redemptionDialogOpen, setRedemptionDialogOpen] = useState(false);
  const [mySeries, setMySeries] = useState<any[]>([]);
  const [loadingSeries, setLoadingSeries] = useState(true);

  // Fetch ONLY this investor's series using their lenderId
  React.useEffect(() => {
    const fetchMySeries = async () => {
      if (!userData?.lenderId) {
        console.log('No lenderId in userData');
        setMySeries([]);
        setLoadingSeries(false);
        return;
      }

      try {
        setLoadingSeries(true);
        const { getSeriesByLender } = await import('../../services/seriesService');
        const allSeries = await getSeriesByLender(userData.lenderId);
        const activeSeries = allSeries.filter((s: Series) => s.status === 'active');
        setMySeries(activeSeries);
      } catch (error) {
        console.error('Error fetching series');
        setMySeries([]);
      } finally {
        setLoadingSeries(false);
      }
    };

    fetchMySeries();
  }, [userData?.lenderId]);
  
  // Get lenderId for redemptions
  const lenderId = userData?.lenderId;
  
  // Get redemptions for this lender
  const { redemptions, loading: loadingRedemptions } = useRedemptions(lenderId, true);
  
  const loading = loadingLender || loadingSeries;

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
      // Force navigate anyway
      navigate('/login');
    }
  };
  
  const totalInvested = mySeries.reduce((sum: number, s: Series) => sum + s.principalAmount, 0);
  const totalCurrentValue = mySeries.reduce((sum: number, s: Series) => sum + s.currentNAV, 0);
  const totalReturn = totalInvested > 0 ? ((totalCurrentValue - totalInvested) / totalInvested) * 100 : 0;

  const MetricCard = ({ title, value, subtitle, icon: Icon, color }: any) => (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography color="text.secondary" variant="body2" gutterBottom>
              {title}
            </Typography>
            <Typography variant="h4" sx={{ color }}>
              {value}
            </Typography>
            {subtitle && (
              <Typography variant="caption" color="text.secondary">
                {subtitle}
              </Typography>
            )}
          </Box>
          <Icon sx={{ fontSize: 48, color, opacity: 0.3 }} />
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
      {/* Top AppBar */}
      <AppBar position="static" elevation={0}>
        <Toolbar>
          <Typography variant="h6" sx={{ flexGrow: 1, color: 'primary.main' }}>
            Maverick Capital - Investor Portal
          </Typography>
          <Typography variant="body2" sx={{ mr: 2 }}>
            {myLender?.fullName}
          </Typography>
          <Button
            color="inherit"
            startIcon={<Logout />}
            onClick={handleLogout}
          >
            Logout
          </Button>
        </Toolbar>
      </AppBar>

      {/* Main Content */}
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        {loading ? (
          <Typography>Loading data...</Typography>
        ) : (
          <>
            {/* Key Metrics */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 3, mb: 4 }}>
              <MetricCard
                title="Total Invested"
                value={formatCurrency(totalInvested)}
                icon={AccountBalance}
                color="#FFFFFF"
              />
              <MetricCard
                title="Current Value"
                value={formatCurrency(totalCurrentValue)}
                icon={TrendingUp}
                color="#FFD700"
              />
              <MetricCard
                title="Total Return"
                value={formatPercentage(totalReturn / 100)}
                subtitle={formatCurrency(totalCurrentValue - totalInvested)}
                icon={TrendingUp}
                color={totalReturn >= 0 ? '#4CAF50' : '#F44336'}
              />
            </Box>

            {/* Redemption Button */}
            <Box sx={{ mb: 4, display: 'flex', justifyContent: 'center' }}>
              <Button
                variant="contained"
                size="large"
                startIcon={<MonetizationOn />}
                onClick={() => setRedemptionDialogOpen(true)}
                disabled={mySeries.length === 0}
                sx={{
                  bgcolor: '#FFD700',
                  color: '#000',
                  px: 4,
                  py: 1.5,
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  '&:hover': { 
                    bgcolor: '#FFC700',
                    transform: 'scale(1.02)',
                  },
                  transition: 'all 0.2s',
                  boxShadow: '0 4px 12px rgba(255, 215, 0, 0.3)',
                }}
              >
                Request Redemption
              </Button>
            </Box>

            {/* Redemption Status Section */}
            {!loadingRedemptions && redemptions && redemptions.length > 0 && (
              <Box sx={{ mb: 4 }}>
                {redemptions.map((redemption) => {
                  const getStatusColor = (): 'warning' | 'info' | 'success' | 'error' => {
                    switch (redemption.status) {
                      case 'pending': return 'warning';
                      case 'processing': return 'info';
                      case 'completed': return 'success';
                      case 'cancelled': return 'error';
                      default: return 'warning';
                    }
                  };

                  const getStatusIcon = () => {
                    switch (redemption.status) {
                      case 'pending': return <HourglassEmpty />;
                      case 'processing': return <CheckCircle />;
                      case 'completed': return <CheckCircle />;
                      case 'cancelled': return <Cancel />;
                      default: return <HourglassEmpty />;
                    }
                  };

                  const getStatusMessage = () => {
                    switch (redemption.status) {
                      case 'pending':
                        return 'Your redemption request is being reviewed by the fund manager.';
                      case 'processing':
                        return `Approved! Payment will be processed on ${format(new Date(redemption.redemptionDate), 'MMM dd, yyyy')}.`;
                      case 'completed':
                        return 'Redemption completed. Funds have been paid.';
                      case 'cancelled':
                        return 'Your redemption request was cancelled. Please contact the fund manager.';
                      default:
                        return 'Processing your request...';
                    }
                  };

                  return (
                    <Alert
                      key={redemption.id}
                      severity={getStatusColor()}
                      icon={getStatusIcon()}
                      sx={{ mb: 2 }}
                    >
                      <AlertTitle sx={{ fontWeight: 600 }}>
                        Redemption Request - {redemption.status.toUpperCase()}
                      </AlertTitle>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        {getStatusMessage()}
                      </Typography>
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="caption" sx={{ display: 'block' }}>
                          <strong>Request Date:</strong> {format(new Date(redemption.requestDate), 'MMM dd, yyyy')}
                        </Typography>
                        <Typography variant="caption" sx={{ display: 'block' }}>
                          <strong>Redemption Date:</strong> {format(new Date(redemption.redemptionDate), 'MMM dd, yyyy')} (+30 days)
                        </Typography>
                       <Typography variant="caption" sx={{ display: 'block' }}>
                          <strong>Redemption Value:</strong> {formatCurrency(redemption.redemptionValue)}
                        </Typography>
                        {redemption.isEarlyRedemption && (
                          <Typography variant="caption" sx={{ display: 'block', color: 'warning.main' }}>
                            ⚠️ Early Redemption Fee Applied: {formatCurrency(redemption.earlyRedemptionFee || 0)}
                          </Typography>
                        )}
                      </Box>
                    </Alert>
                  );
                })}
              </Box>
            )}


            {/* Series Breakdown */}
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Your Investment Series
                </Typography>
                <Box sx={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ borderBottom: '2px solid #333' }}>
                        <th style={{ textAlign: 'left', padding: '12px', color: '#FFD700' }}>Issue Date</th>
                        <th style={{ textAlign: 'right', padding: '12px', color: '#FFD700' }}>Principal</th>
                        <th style={{ textAlign: 'right', padding: '12px', color: '#FFD700' }}>Current NAV</th>
                        <th style={{ textAlign: 'right', padding: '12px', color: '#FFD700' }}>Return</th>
                        <th style={{ textAlign: 'center', padding: '12px', color: '#FFD700' }}>Lock-up</th>
                      </tr>
                    </thead>
                    <tbody>
                      {mySeries.map((seriesItem) => {
                        const returns = ((seriesItem.currentNAV - seriesItem.principalAmount) / seriesItem.principalAmount) * 100;
                        const isLocked = isInLockupPeriod(seriesItem.issueDate);
                        
                        return (
                          <tr key={seriesItem.id} style={{ borderBottom: '1px solid #333' }}>
                            <td style={{ padding: '12px' }}>
                              {format(new Date(seriesItem.issueDate), 'dd MMM yyyy')}
                            </td>
                            <td style={{ padding: '12px', textAlign: 'right' }}>
                              {formatCurrency(seriesItem.principalAmount)}
                            </td>
                            <td style={{ padding: '12px', textAlign: 'right' }}>
                              {formatCurrency(seriesItem.currentNAV)}
                            </td>
                            <td style={{ padding: '12px', textAlign: 'right', color: returns >= 0 ? '#4CAF50' : '#F44336' }}>
                              {formatPercentage(returns / 100)}
                            </td>
                            <td style={{ padding: '12px', textAlign: 'center' }}>
                              <Chip
                                icon={isLocked ? <Lock /> : <LockOpen />}
                                label={isLocked ? 'Locked' : 'Available'}
                                size="small"
                                color={isLocked ? 'default' : 'success'}
                                sx={{ 
                                  bgcolor: isLocked ? '#333' : 'rgba(76, 175, 80, 0.2)',
                                  color: isLocked ? '#E0E0E0' : '#4CAF50'
                                }}
                              />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </Box>
              </CardContent>
            </Card>

          </>
        )}
      </Container>

      {/* Redemption Dialog */}
      <RequestRedemptionDialog
        open={redemptionDialogOpen}
        onClose={() => setRedemptionDialogOpen(false)}
        lenderId={myLender?.id || ''}
        series={mySeries}
        onSuccess={() => {
          setRedemptionDialogOpen(false);
          // Could add toast notification here
          alert('Redemption request submitted successfully!');
        }}
      />
    </Box>
  );
};

export default InvestorDashboard;
